import { useState } from "react";
import type { QueryKey } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  History,
  LifeBuoy,
  PackageSearch,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "react-oidc-context";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import type { Order, OrderItem, OrderRequestType, OrderSupportRequest } from "@/lib/api";
import { getCancelOrderErrorMessage } from "@/lib/orderCancellationError";
import { cn } from "@/lib/utils";
import { ItemRequestDetail } from "../OrderSupportRequest/ItemRequestDetail";
import { ItemRequestDialog } from "../OrderSupportRequest/ItemRequestDialog";
import { OrderRequestsList } from "../OrderSupportRequest/OrderRequestsList";
import {
  buildRequestEntries,
  CANCELLATION_REQUEST_TYPE,
  findRequestOfType,
  isCancellationRequest,
  isSingleRequestType,
  type RequestListEntry,
} from "../OrderSupportRequest/supportRequest";
import { useOrderRequestTypes, useOrderSupportRequests } from "../OrderSupportRequest/useOrderSupportRequests";
import { CancellationForm } from "./CancellationForm";
import { CancellationResult, type CancellationRequest } from "./CancellationResult";
import { CancellationNoticeDialog, type CancellationNotice } from "./CancellationNoticeDialog";
import {
  buildCancellationReason,
  CANCELLATION_MODE_COPY,
  type CancellationMode,
  type CancellationReason,
} from "./cancellation";
import { useCancelOrder } from "./useCancelOrder";

type HelpView = "menu" | "form" | "tracking" | "requests" | "requestDetail";

interface HelpOptionProps {
  icon: LucideIcon;
  iconClassName?: string;
  title: string;
  description: string;
  disabled?: boolean;
  onSelect: () => void;
}

const HelpOption = ({ icon: Icon, iconClassName, title, description, disabled, onSelect }: HelpOptionProps) => (
  <li>
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className="group flex w-full items-center gap-4 rounded-lg border px-5 py-4 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
    >
      <Icon className={cn("h-5 w-5 shrink-0 text-muted-foreground", iconClassName)} aria-hidden="true" />
      <span className="flex flex-1 flex-col gap-1">
        <span className="text-[15px] font-semibold leading-snug">{title}</span>
        <span className="text-sm leading-relaxed text-muted-foreground">{description}</span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground group-disabled:hidden" aria-hidden="true" />
    </button>
  </li>
);

const BackBar = ({ onBack }: { onBack: () => void }) => (
  <div className="border-b px-7 py-3">
    <Button
      variant="ghost"
      size="sm"
      onClick={onBack}
      className="-ml-3 text-muted-foreground hover:bg-transparent hover:text-foreground"
    >
      <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
      Voltar
    </Button>
  </div>
);

interface OrderCancellationSectionProps {
  orderId: string;
  order: Order;
  /** Query do detalhe do pedido — atualizada com o retorno do cancelamento. */
  orderQueryKey: QueryKey;
  /** Status do pedido ainda carregando — até lá `blockedReason` não é confiável. */
  isEligibilityPending: boolean;
  /** Por que o pedido não aceita solicitação de cancelamento (programado, em rota, entregue…); `null` quando aceita. */
  blockedReason: string | null;
  /** Já existe retorno de processamento do ERP — o backend também trata isso como "integrado". */
  hasExternalStatus: boolean;
  /** Status exibido no detalhe do pedido — enviado junto ao motivo pra constar no chamado do Zendesk. */
  orderStatusLabel: string;
  onStartNewOrder: () => void;
  /** Itens exibidos no pedido — opções do seletor das solicitações por item. */
  items: OrderItem[];
  /** Mesma informação da coluna "Tipo" da tabela de produtos. */
  getItemUsage?: (item: OrderItem) => string | null | undefined;
  /** Por que o pedido não aceita solicitação por item (não entregue, cancelado, programado); `null` quando aceita. */
  itemRequestBlockedReason: string | null;
  /** `false` quando o pedido veio de `scheduledOrdersApi` (o id não é de `purchase_orders` e `/requests` daria 404). */
  canLoadRequests: boolean;
}

/**
 * Seção "Ajuda com o meu pedido" do detalhe do pedido (backoffice e portal do cliente): começa recolhida e,
 * aberta, leva ao cancelamento (pedido inteiro), às solicitações por item (troca, checklist e outros tipos de
 * `GET /orders/request-types`) e à lista "Solicitações do pedido".
 * O cancelamento aparece mesmo quando o pedido não aceita mais cancelamento: nesse caso a solicitação só avisa
 * (modal) por que não é possível. O desfecho vem sempre do pedido (`cancelledAt` / `cancellationTicketId`), então
 * recarregar a página mantém o acompanhamento disponível.
 */
export const OrderCancellationSection = ({
  orderId,
  order,
  orderQueryKey,
  isEligibilityPending,
  blockedReason,
  hasExternalStatus,
  orderStatusLabel,
  onStartNewOrder,
  items,
  getItemUsage,
  itemRequestBlockedReason,
  canLoadRequests,
}: OrderCancellationSectionProps) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<HelpView>("menu");
  const [lastRequest, setLastRequest] = useState<CancellationRequest | null>(null);
  const [notice, setNotice] = useState<CancellationNotice | null>(null);
  const [itemRequestType, setItemRequestType] = useState<OrderRequestType | null>(null);
  // Muda a cada abertura do modal pra ele começar sempre vazio.
  const [itemRequestSession, setItemRequestSession] = useState(0);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const cancelOrder = useCancelOrder(orderId, orderQueryKey);
  const requestTypes = useOrderRequestTypes();

  const hasOutcome = !!order.cancelledAt || !!order.cancellationTicketId;
  // Assim que o envio (ou um 409 de "já cancelado") grava o desfecho no pedido, o formulário dá lugar ao acompanhamento.
  const activeView: HelpView = view === "form" && hasOutcome ? "tracking" : view;
  const isShowingRequests = activeView === "requests" || activeView === "requestDetail";
  // Divergência: uma solicitação por pedido. Com o pedido aceitando solicitação por item, a lista já é buscada ao
  // abrir a seção pra que o menu saiba se o tipo já tem solicitação registrada.
  const checksExistingRequests = canLoadRequests && !itemRequestBlockedReason;
  const supportRequests = useOrderSupportRequests(
    orderId,
    (canLoadRequests && isShowingRequests) || (checksExistingRequests && isOpen)
  );
  const isCheckingExistingRequests = checksExistingRequests && supportRequests.isLoading;

  // Esconde (sem desmontar, pra não perder `lastRequest`) até o status do pedido chegar.
  if (isEligibilityPending && !hasOutcome) return null;

  // Mesmo critério do backend pra bloquear o cancelamento direto e abrir chamado.
  const mode: CancellationMode = order.webhookSentAt || hasExternalStatus ? "ticket" : "direct";
  const itemRequestTypes = requestTypes.data?.filter((type) => type.requiresItem) ?? [];
  const cancellationTitle = requestTypes.data?.find((type) => type.code === CANCELLATION_REQUEST_TYPE)?.title;
  const requestEntries = buildRequestEntries(supportRequests.data ?? [], order, cancellationTitle);
  const selectedRequest =
    selectedTicketId !== null
      ? supportRequests.data?.find((request) => request.zendeskTicketId === selectedTicketId)
      : undefined;

  const handleSubmit = ({ reason, details }: CancellationReason) => {
    const trimmedDetails = details?.trim() || undefined;
    setLastRequest({ reason, details: trimmedDetails, requestedBy: user?.profile?.email });
    cancelOrder.mutate(buildCancellationReason({ reason, details: trimmedDetails, orderStatus: orderStatusLabel }), {
      // Roda depois do `onSuccess` do hook (pedido já no cache). Voltar ao menu aqui evita a troca
      // automática pro acompanhamento: o modal confirma e "Continuar" deixa o usuário no menu.
      onSuccess: (updatedOrder) => {
        setView("menu");
        const ticketId = updatedOrder.cancellationTicketId;
        setNotice({
          tone: "success",
          title: ticketId ? "Solicitação registrada" : "Pedido cancelado",
          description: ticketId ? (
            <>
              Sua solicitação foi registrada com o ticket{" "}
              <strong className="font-semibold text-foreground">#{ticketId}</strong>.
            </>
          ) : (
            "Seu pedido foi cancelado com sucesso."
          ),
          actionLabel: "Continuar",
        });
      },
    });
  };

  const handleRequestCancellation = () => {
    if (!blockedReason) {
      setView("form");
      return;
    }
    setNotice({
      tone: "warning",
      title: "Não é possível solicitar o cancelamento",
      description: blockedReason,
      actionLabel: "Entendi",
    });
  };

  const handleRequestItem = (type: OrderRequestType) => {
    // Mesmo padrão do cancelamento: a opção aparece e, se o pedido não aceita, o modal explica sem chamar a API.
    if (itemRequestBlockedReason) {
      setNotice({
        tone: "warning",
        title: "Não é possível abrir esta solicitação",
        description: itemRequestBlockedReason,
        actionLabel: "Entendi",
      });
      return;
    }
    // A troca aceita várias por pedido; a divergência, uma só — já registrada, a opção fica desabilitada. Se a lista
    // não carregou, o modal abre e o 409 do back avisa e leva à solicitação existente.
    setItemRequestSession((session) => session + 1);
    setItemRequestType(type);
  };

  const handleItemRequestCreated = (created: OrderSupportRequest) => {
    setItemRequestType(null);
    setNotice({
      tone: "success",
      title: "Solicitação registrada",
      description: (
        <>
          Sua solicitação foi registrada com o ticket{" "}
          <strong className="font-semibold text-foreground">#{created.zendeskTicketId}</strong>. Acompanhe em
          "Acompanhar minhas solicitações".
        </>
      ),
      actionLabel: "Continuar",
    });
  };

  // 409: abre o acompanhamento da solicitação que já está aberta (ou a lista, sem o número do ticket).
  const handleViewExistingRequest = (zendeskTicketId: number | null) => {
    setItemRequestType(null);
    setSelectedTicketId(zendeskTicketId);
    setView(zendeskTicketId !== null ? "requestDetail" : "requests");
  };

  const handleSelectEntry = (entry: RequestListEntry) => {
    if (entry.kind === "directCancellation" || isCancellationRequest(entry.request)) {
      setView("tracking");
      return;
    }
    setSelectedTicketId(entry.request.zendeskTicketId);
    setView("requestDetail");
  };

  const handleBackToMenu = () => {
    setView("menu");
    cancelOrder.reset();
  };

  // Nos avisos a seção termina no menu (no bloqueio ela já está lá e o `reset` não tem efeito).
  const handleCloseNotice = () => {
    setNotice(null);
    handleBackToMenu();
  };

  // O conteúdo desmonta ao recolher, então reabrir sempre começa pelo menu.
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) handleBackToMenu();
  };

  const requestsList = (
    <OrderRequestsList
      entries={requestEntries}
      isLoading={supportRequests.isLoading}
      isError={supportRequests.isError}
      isFetching={supportRequests.isFetching}
      onRetry={() => supportRequests.refetch()}
      onSelect={handleSelectEntry}
    />
  );

  const renderRequestDetail = () => {
    if (selectedRequest) {
      return (
        <ItemRequestDetail
          orderId={orderId}
          request={selectedRequest}
          orderItems={items}
          getItemUsage={getItemUsage}
          isRefreshing={supportRequests.isFetching}
          onRefresh={() => supportRequests.refetch()}
        />
      );
    }
    if (supportRequests.isLoading) {
      return (
        <div className="flex flex-col gap-4 p-7" aria-busy="true" aria-label="Carregando solicitação">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-40 w-full rounded-[10px]" />
        </div>
      );
    }
    // Ticket fora da lista (ou lista indisponível): mostra a lista, com os estados dela.
    return requestsList;
  };

  return (
    <Card id="order-cancellation" className="overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={handleOpenChange} disabled={cancelOrder.isPending}>
        <h2>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-4 px-7 py-5 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed"
            >
              <span className="flex items-center gap-3.5">
                <LifeBuoy className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="flex flex-col gap-1">
                  <span className="text-[19px] font-bold leading-tight">Ajuda com o meu pedido</span>
                </span>
              </span>
              <ChevronDown
                className={cn("h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200", isOpen && "rotate-180")}
                aria-hidden="true"
              />
            </button>
          </CollapsibleTrigger>
        </h2>

        <CollapsibleContent className="border-t">
          {activeView === "form" ? (
            <CancellationForm
              mode={mode}
              isPending={cancelOrder.isPending}
              errorMessage={cancelOrder.isError ? getCancelOrderErrorMessage(cancelOrder.error) : null}
              onSubmit={handleSubmit}
              onKeep={handleBackToMenu}
            />
          ) : activeView === "tracking" ? (
            <>
              <BackBar onBack={() => setView("requests")} />
              <CancellationResult
                orderId={orderId}
                order={order}
                lastRequest={lastRequest}
                onStartNewOrder={onStartNewOrder}
              />
            </>
          ) : activeView === "requests" ? (
            <>
              <BackBar onBack={handleBackToMenu} />
              {requestsList}
            </>
          ) : activeView === "requestDetail" ? (
            <>
              <BackBar onBack={() => setView("requests")} />
              {renderRequestDetail()}
            </>
          ) : (
            <ul className="flex flex-col gap-3 px-7 py-6">
              <HelpOption
                icon={XCircle}
                iconClassName="text-destructive"
                title="Solicitar cancelamento"
                description={
                  hasOutcome
                    ? "Já existe uma solicitação de cancelamento para este pedido."
                    : CANCELLATION_MODE_COPY[mode].description
                }
                disabled={hasOutcome}
                onSelect={handleRequestCancellation}
              />
              {requestTypes.isLoading || isCheckingExistingRequests ? (
                [0, 1].map((index) => (
                  <li key={index}>
                    <Skeleton className="h-[74px] w-full rounded-lg" />
                  </li>
                ))
              ) : requestTypes.isError ? (
                <li className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed px-5 py-4 text-sm text-muted-foreground">
                  <AlertCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
                  <span className="flex-1">Não foi possível carregar as outras opções de ajuda.</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => requestTypes.refetch()}
                    disabled={requestTypes.isFetching}
                  >
                    Tentar novamente
                  </Button>
                </li>
              ) : (
                itemRequestTypes.map((type) => {
                  const existingRequest = isSingleRequestType(type)
                    ? findRequestOfType(supportRequests.data ?? [], type.code)
                    : undefined;
                  return (
                    <HelpOption
                      key={type.code}
                      icon={PackageSearch}
                      iconClassName="text-primary"
                      title={type.title}
                      description={
                        existingRequest
                          ? `Já existe uma solicitação (ticket #${existingRequest.zendeskTicketId}). Acompanhe em "Acompanhar minhas solicitações".`
                          : ""
                      }
                      disabled={!!existingRequest}
                      onSelect={() => handleRequestItem(type)}
                    />
                  );
                })
              )}
              <HelpOption
                icon={History}
                title="Acompanhar minhas solicitações"
                description=""
                onSelect={() => setView("requests")}
              />
            </ul>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* O tipo escolhe a variante do formulário: conferência (quantidade recebida) ou troca (descrição livre). */}
      <ItemRequestDialog
        key={`item-${itemRequestSession}`}
        orderId={orderId}
        orderNumber={order.orderNumber}
        requestType={itemRequestType}
        items={items}
        getItemUsage={getItemUsage}
        onClose={() => setItemRequestType(null)}
        onCreated={handleItemRequestCreated}
        onViewExisting={handleViewExistingRequest}
      />
      <CancellationNoticeDialog notice={notice} onClose={handleCloseNotice} />
    </Card>
  );
};
